import {
	type CaptureFileJSONObject,
	type CollectionFieldLocationProviderEdited,
	type CollectionFieldLocationProviderGPS,
	type CollectionFieldLocationProviderManual,
	IkeFieldType,
	type LocationAltitude,
	type LocationData,
	type LocationDataRaw,
} from '@ikegps/core-api';
import { IkeObject } from '../../../util/object';
import type { FormModelField } from '../../form/form.field';
import { CollectionModelField, ModelMutator } from '../collection.field';
import { registerFieldType } from './field.registry';
import type { CaptureProvider } from './provider/capture.provider';

const DEFAULT_LOCATION_DATA: LocationData = {
	latitude: 0,
	longitude: 0,
	altitude: 0,
	accuracy: 0,
};

export type ModelFieldStateLocation =
	| ModelFieldStateLocationManual
	| ModelFieldStateLocationGps
	| ModelFieldStateLocationTrueSize
	| ModelFieldStateLocationEdited;

export type LocationDataValue = {
	value: LocationData;
};

export type ModelFieldStateLocationManual = CollectionFieldLocationProviderManual & LocationDataValue;
export type ModelFieldStateLocationGps = CollectionFieldLocationProviderGPS & LocationDataValue;
export type ModelFieldStateLocationEdited = CollectionFieldLocationProviderEdited & LocationDataValue;

export interface ModelFieldStateLocationTrueSize {
	provider: 'truesize';
	captureId: string;
	datum?: string;
}

const LocationRange = {
	latitude: { min: -90, max: 90 },
	longitude: { min: -180, max: 180 },
};

export function extractLocationRaw(raw?: Partial<LocationDataRaw>): LocationDataRaw {
	// To keep type safety around the raw location
	// data we need to assign only the keys we care about and are not null
	return {
		antennaHeight: raw.antennaHeight,
		fix: raw.fix,
		module: raw.module,
		capturedAt: raw.capturedAt,
		undulation: raw.undulation,
		datum: raw.datum,
	};
}

export class ModelMutatorLocation extends ModelMutator<FormModelField, CollectionModelFieldLocation> {
	manual(newValue: LocationData) {
		if (!CollectionModelFieldLocation.isValidLocation(newValue)) {
			this.clear();
		} else {
			const value = { latitude: newValue.latitude, longitude: newValue.longitude, altitude: newValue.altitude };
			this.state.setFieldValue(this.field.id, { provider: 'manual', value });
		}
	}

	edited(newValue: LocationData) {
		if (!CollectionModelFieldLocation.isValidLocation(newValue)) {
			this.clear();
		} else {
			const value = { latitude: newValue.latitude, longitude: newValue.longitude, altitude: newValue.altitude };
			this.state.setFieldValue(this.field.id, { provider: 'edited', value });
		}
	}

	gps(newValue: LocationData, raw?: Partial<LocationDataRaw>) {
		if (!CollectionModelFieldLocation.isValidLocation(newValue)) {
			this.clear();
			return;
		}

		const value: LocationData = {
			latitude: newValue.latitude,
			longitude: newValue.longitude,
			altitude: newValue.altitude,
		};
		if (newValue.accuracy != null) {
			value.accuracy = newValue.accuracy;
		}

		const stateValue: ModelFieldStateLocationGps = { provider: 'gps', value };
		if (raw) {
			IkeObject.assignNotNull(stateValue, extractLocationRaw(raw));
		}

		this.state.setFieldValue(this.field.id, stateValue);
	}

	capture(captureId: string, datum?: string) {
		this.state.setFieldValue(this.field.id, { provider: 'truesize', captureId, datum });
	}

	override clear() {
		this.state.setFieldValue(this.field.id, null);
	}
}

export class CollectionModelFieldLocation
	extends CollectionModelField<FormModelField, ModelFieldStateLocation>
	implements CaptureProvider
{
	static readonly TYPE: IkeFieldType = IkeFieldType.LOCATION;
	static readonly PROVIDER_MANUAL = 'manual';
	static readonly PROVIDER_GPS = 'gps';
	static readonly PROVIDER_TRUE_SIZE = 'truesize';
	static readonly PROVIDER_EDITED = 'edited';

	static isLocationField(f: CollectionModelField): f is CollectionModelFieldLocation {
		return f.schema.type === CollectionModelFieldLocation.TYPE;
	}
	static isValidLocation(l: LocationData): boolean {
		if (l == null) {
			return false;
		}

		if (isNaN(l.latitude) || isNaN(l.longitude)) {
			return false;
		}

		if (l.latitude > LocationRange.latitude.max || l.latitude < LocationRange.latitude.min) {
			return false;
		}

		if (l.longitude > LocationRange.longitude.max || l.longitude < LocationRange.longitude.min) {
			return false;
		}

		return l.latitude !== DEFAULT_LOCATION_DATA.latitude || l.longitude !== DEFAULT_LOCATION_DATA.longitude;
	}

	private _Mutator: ModelMutatorLocation;
	get Mutator() {
		if (this._Mutator == null) {
			this._Mutator = new ModelMutatorLocation(this);
		}
		return this._Mutator;
	}

	override get value(): LocationData {
		if (this.stateValue == null) {
			return {
				latitude: 0,
				longitude: 0,
				altitude: 0,
				accuracy: 0,
			};
		}

		if (this.isEdited()) {
			return this.stateValue.value;
		}

		if (this.isTrueSize()) {
			const [capture] = this.getCaptures();
			if (capture == null) {
				return null;
			}
			return capture.target;
		}

		if (this.isGPS()) {
			return this.stateValue.value;
		}

		if (this.isManual()) {
			return this.stateValue.value;
		}

		return null;
	}

	/**
	 * Get the raw location data calculating alternative altitudes
	 *
	 * @remarks This cannot be null
	 */
	get raw(): Partial<LocationDataRaw> & Partial<LocationAltitude> {
		if (!this.isGPS()) {
			return {};
		}

		const stateValue = this.stateValue;
		const isAntennaHeightMissing = stateValue.antennaHeight == null || stateValue.antennaHeight === 0;
		if (isAntennaHeightMissing) {
			return {
				...extractLocationRaw(stateValue),
				deviceAltitude: stateValue.value.altitude,
				antennaHeight: undefined, // Destroy antennaHeight if it is 0
			};
		}

		return {
			...extractLocationRaw(stateValue),
			deviceAltitude: stateValue.antennaHeight + stateValue.value.altitude,
			groundAltitude: stateValue.value.altitude,
			antennaHeight: stateValue.antennaHeight,
		};
	}

	get provider(): 'manual' | 'gps' | 'truesize' | 'edited' {
		const stateValue = this.stateValue;
		if (stateValue == null || stateValue.provider == null) {
			return 'manual';
		}
		return stateValue.provider;
	}

	isManual(): this is CollectionModelField<FormModelField, ModelFieldStateLocationManual> {
		return this.provider === CollectionModelFieldLocation.PROVIDER_MANUAL;
	}

	isEdited(): this is CollectionModelField<FormModelField, ModelFieldStateLocationEdited> {
		return this.provider === CollectionModelFieldLocation.PROVIDER_EDITED;
	}

	isTrueSize(): this is CollectionModelField<FormModelField, ModelFieldStateLocationTrueSize> {
		return this.provider === CollectionModelFieldLocation.PROVIDER_TRUE_SIZE;
	}

	isGPS(): this is CollectionModelField<FormModelField, ModelFieldStateLocationGps> {
		return this.provider === CollectionModelFieldLocation.PROVIDER_GPS;
	}

	getOwnedCaptures(): CaptureFileJSONObject[] {
		return this.getCaptures();
	}

	getCaptures(): CaptureFileJSONObject[] {
		return this.getCaptureIds()
			.map((c) => this.collection.state.getCapture(c))
			.filter((c) => !!c);
	}

	getOwnedCaptureIds(): string[] {
		return this.getCaptureIds();
	}
	getCaptureIds(): string[] {
		if (this.isTrueSize()) {
			return [this.stateValue.captureId];
		}
		return [];
	}

	hasValue(): boolean {
		return CollectionModelFieldLocation.isValidLocation(this.value);
	}
}

registerFieldType(IkeFieldType.LOCATION, (parent, schema) => new CollectionModelFieldLocation(parent, schema));
